'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, Search, HeadphonesIcon, User, Package, Utensils } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { useUserStore } from '@/store/user-store';
import { useAuth } from '@/hooks/use-auth';
import { useLoginModal } from '@/components/auth/login';
import { storageService } from "@/services/storage.service"
import { API } from '@/constants/api';
import { useCartStore, CartItem } from '@/store/cart-store';
import { useTCPParams } from "@/store/tcp-params-store";


export const MobileBottomNav: React.FC = () => {
  const pathname = usePathname();
  const {openLoginModal} = useLoginModal();
   const userStore = useUserStore();
   const cartStore = useCartStore();
   const { setIsSheetOpen, isSheetOpen } = useUserStore();

  const { params: tcpParams } = useTCPParams();
  const isTcpApp = (tcpParams?.source || '').toLowerCase() === 'tcp';
   console.log("MobileBottomNav - Initial isSheetOpen:", isSheetOpen);

    // Get Next-Auth authentication state
    const { status, session, accessToken, signOut, update } = useAuth();
    
    // Combined authentication state from both auth systems
    const nextAuthAuthenticated = status === 'authenticated';
    const zustandAuthenticated = userStore.isAuthenticated;
    const isAuthenticated = nextAuthAuthenticated || zustandAuthenticated;
    
    // Add handleLogout function
    const handleLogout = async () => {
      try {
        console.log('[MobileNav] Logging out user');
        await storageService.removeItem(`marketplace-vendor-login-data-${API.MARKETPLACE_REFERENCE_ID}`);
        localStorage.removeItem('app_access_token');
        // if (nextAuthAuthenticated) {
        await signOut({ redirect: false });
        await update();
        // }
        
        // if (zustandAuthenticated) {
        userStore.logout();
        // }
        window.location.reload();
        window.location.href = '/';
        cartStore.getState().clearCart();
        setIsSheetOpen(false);
      } catch (error) {
        console.error('[MobileNav] Error during logout:', error);
        window.location.href = '/';
      }
    };
  
  const isActive = (path: string) => {
    if (path === '/' && pathname === '/') return true;
    if (path !== '/' && pathname?.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden">
      <div className="flex items-center justify-between px-4 py-3">
        <Link href="/" className="flex flex-col items-center space-y-1">
          <div 
            className={`p-1 rounded-full ${
              isActive('/') ? 'text-[#900f21]' : 'text-gray-500'
            }`}
          >
            <Home className="h-6 w-6" />
          </div>
          <span 
            className={`text-xs ${
              isActive('/') ? 'text-[#900f21] font-medium' : 'text-gray-500'
            }`}
          >
            Home
          </span>
        </Link>
        
        {isAuthenticated && (
        <Link href="/search" className="flex flex-col items-center space-y-1">
          <div 
            className={`p-1 rounded-full ${
              isActive('/search') ? 'text-[#900f21]' : 'text-gray-500'
            }`}
          >
            <Search className="h-6 w-6" />
          </div>
          <span 
            className={`text-xs ${
              isActive('/search') ? 'text-[#900f21] font-medium' : 'text-gray-500'
            }`}
          >
            Search
          </span>
        </Link>)}
        
        <Link href="/support" className="flex flex-col items-center space-y-1">
          <div 
            className={`p-1 rounded-full ${
              isActive('/support') ? 'text-[#900f21]' : 'text-gray-500'
            }`}
          >
            <HeadphonesIcon className="h-6 w-6" />
          </div>
          <span 
            className={`text-xs ${
              isActive('/support') ? 'text-[#900f21] font-medium' : 'text-gray-500'
            }`}
          >
            Support
          </span>
        </Link>

        
        {!isAuthenticated && (
                      <div >
                       <Link  href="/profile" prefetch={true}> 
                      <div
                        className="h-9 w-9 bg-[#900f21] text-white rounded-full flex items-center justify-center text-base font-medium cursor-pointer hover:bg-[#8a6d3b]/90 transition-colors"
                        role="button"
                        aria-label="Go to profile"
                      >
                        <User size={18} />
                      </div>
                      </Link>
                    </div>)}
        {isAuthenticated && (
          <Sheet open={userStore.isSheetOpen} onOpenChange={userStore.setIsSheetOpen}>
          <SheetTrigger asChild>
            <button className="flex flex-col items-center space-y-1">
              <div 
                className={`p-1 rounded-full ${
                  isActive('/profile') ? 'text-[#900f21]' : 'text-gray-500'
                }`}
              >
                <User className="h-6 w-6" />
              </div>
              <span 
                className={`text-xs ${
                  isActive('/profile') ? 'text-[#900f21] font-medium' : 'text-gray-500'
                }`}
              >
                Account
              </span>
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85%] sm:w-[385px]">
          <div className="flex flex-col h-full bg-white z-[2147483647]">
              {/* Profile Section */}
              {!isTcpApp && (
              <div className="px-4 py-6 border-b border-gray-100">
                <SheetClose asChild>
                  <Link
                    href="/profile"
                    className="flex items-center space-x-3 text-gray-900 hover:text-[#900f21] font-medium text-lg"
                  >
                    <User className="h-6 w-6" />
                    <span>Profile</span>
                  </Link>
                </SheetClose>
              </div>
              )}
              {/* Menu Options */}
              <nav className="flex flex-col px-4 py-2">
                <SheetClose asChild>
                  <Link
                    href="/restaurants"
                    className="flex items-center space-x-3 text-gray-900 hover:text-[#900f21] font-medium text-lg py-3 border-b border-gray-100"
                  >
                    <Utensils className="h-5 w-5" />
                    <span>Restaurants</span>
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    href="/orders"
                    className="flex items-center space-x-3 text-gray-900 hover:text-[#900f21] font-medium text-lg py-3 border-b border-gray-100"
                  >
                    <Package className="h-5 w-5" />
                    <span>Orders</span>
                  </Link>
                </SheetClose>
              </nav>
              {/* Logout Button - Adjusted spacing */}
              <div className="mt-auto border-t border-gray-100">
              {!isTcpApp && (
                <SheetClose asChild>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center px-4 py-3 text-red-600 hover:bg-red-50 font-medium transition-colors"
                  >
                    Logout
                  </button>
                </SheetClose>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>
        )}
        
      </div>
    </div>
  );
};

export default MobileBottomNav;
